(function () {
  if (window.__cartGiftInit) return;
  window.__cartGiftInit = true;

  const NORMALIZE_DEBOUNCE_MS = 150;
  let _normalizing = false;
  let _normalizeScheduled = false;

  const productTagsCache = new Map(); // Cache de tags por producto

  // ---------------------
  // 🔧 UTILS
  // ---------------------
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const fetchJSON = async (url, opts = {}) => {
    const ctrl = new AbortController();
    const id = setTimeout(() => ctrl.abort(), 8000);
    try {
      const res = await fetch(url, { credentials: 'same-origin', signal: ctrl.signal, ...opts });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return JSON.parse(await res.text());
    } finally {
      clearTimeout(id);
    }
  };

  const getCart = () => fetchJSON('/cart.js', { headers: { 'Accept': 'application/json' } });

  const getHandleFromLineItem = (item) => {
    if (item.handle) return item.handle;
    if (item.url) {
      const m = item.url.match(/\/products\/([^/?#]+)/i);
      return m ? decodeURIComponent(m[1]) : null;
    }
    return null;
  };

  const getTagsForHandle = async (handle) => {
    if (!handle) return [];
    if (productTagsCache.has(handle)) return productTagsCache.get(handle);
    const prod = await fetchJSON(`/products/${handle}.js`);
    const tags = Array.isArray(prod.tags) ? prod.tags : (typeof prod.tags === 'string' ? prod.tags.split(',').map(t => t.trim()) : []);
    productTagsCache.set(handle, tags);
    return tags;
  };

  const isGiftLine = async (item, tagsMaybe) => {
    const props = item.properties || {};
    if (props._is_free_gift === 'true' || props.is_free_gift === 'true') return true;
    const handle = getHandleFromLineItem(item);
    const tags = tagsMaybe || await getTagsForHandle(handle);
    return tags.includes('free_gift');
  };

  const getActiveVendors = async () => {
    const cart = await getCart();
    const vendors = new Set();
    for (const item of (cart.items || [])) {
      if (await isGiftLine(item)) continue;
      if (item.vendor) vendors.add(item.vendor.trim());
    }
    return Array.from(vendors);
  };

  // ---------------------
  // 🎁 LÓGICA DE REGALOS
  // ---------------------
  const addVariant = async (variantId, props = {}) => {
    const btn = document.querySelector(`[data-gift-add][data-variant-id="${variantId}"]`);
    const handle = btn?.closest('[data-gift-product]')?.getAttribute('data-gift-product');

    if (!handle) {
      console.warn('[GiftGuard] No se pudo verificar handle del regalo. Denegado.');
      throw new Error('Regalo no permitido');
    }

    const [activeVendors, tags, prod] = await Promise.all([
      getActiveVendors(),
      getTagsForHandle(handle),
      fetchJSON(`/products/${handle}.js`)
    ]);

    const isFreeGift = tags.includes('free_gift');
    const vendor = prod.vendor?.trim();
    const hasMatch = isFreeGift && activeVendors.includes(vendor);

    if (!hasMatch) {
      console.warn('[GiftGuard] Intento de añadir regalo NO permitido.', { handle, tags, activeVendors });
      throw new Error('Regalo no permitido');
    }

    const body = new URLSearchParams();
    body.append('id', variantId);
    body.append('quantity', '1');
    const mergedProps = { _is_free_gift: 'true', ...props };
    for (const [k, v] of Object.entries(mergedProps)) {
      body.append(`properties[${k}]`, String(v));
    }

    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body
    });
  };

  const removeLineByKey = (lineKey) => changeLineQtyByKey(lineKey, 0);

  const changeLineQtyByKey = (lineKey, qty) => {
    const body = new URLSearchParams({ id: lineKey, quantity: String(qty) });
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body
    });
  };

  const filterGiftOptionsByProviders = async (root = document) => {
    try {
      const activeVendors = await getActiveVendors();
      const cards = qsa('[data-gift-product]', root);
      for (const card of cards) {
        const handle = card.getAttribute('data-gift-product');
        if (!handle) {
          card.style.display = 'none';
          card.setAttribute('data-allowed', 'false');
          continue;
        }

        try {
          const [tags, prod] = await Promise.all([
            getTagsForHandle(handle),
            fetchJSON(`/products/${handle}.js`)
          ]);
          const isFreeGift = tags.includes('free_gift');
          const vendor = prod.vendor?.trim();
          const allowed = isFreeGift && activeVendors.includes(vendor);

          card.style.display = allowed ? '' : 'none';
          card.setAttribute('data-allowed', allowed ? 'true' : 'false');
        } catch (err) {
          console.warn('[GiftGuard] Error cargando datos regalo', handle, err);
          card.style.display = 'none';
          card.setAttribute('data-allowed', 'false');
        }
      }
    } catch (err) {
      console.warn('[GiftGuard] Filtro de regalos falló:', err);
    }
  };

  const normalizeCart = async (thresholdCents, removeIfBelow) => {
    if (_normalizing) {
      _normalizeScheduled = true;
      return;
    }

    _normalizing = true;
    let changed = false;

    try {
      const cart = await getCart();
      const activeVendors = new Set();
      const linesWithData = [];

      for (const item of cart.items || []) {
        const handle = getHandleFromLineItem(item);
        const tags = await getTagsForHandle(handle);
        const gift = await isGiftLine(item, tags);
        linesWithData.push({ item, handle, tags, gift });
        if (!gift && item.vendor) activeVendors.add(item.vendor.trim());
      }

      const giftLines = linesWithData.filter(x => x.gift).map(x => x.item);
      const paidLines = linesWithData.filter(x => !x.gift).map(x => x.item);

      const subtotal = paidLines.reduce((acc, it) => acc + (it.final_line_price || it.line_price || 0), 0);
      const atOrAbove = subtotal >= (thresholdCents || 0);

      // 1) Dedupe regalos
      for (let i = 1; i < giftLines.length; i++) {
        try { await removeLineByKey(giftLines[i].key); changed = true; } catch (e) { }
      }

      // 2) Forzar qty = 1
      if (giftLines[0]?.quantity !== 1) {
        try { await changeLineQtyByKey(giftLines[0].key, 1); changed = true; } catch (e) { }
      }

      // 3) Eliminar regalos con vendor no válido
      for (const g of giftLines) {
        const handle = getHandleFromLineItem(g);
        const [tags, prod] = await Promise.all([
          getTagsForHandle(handle),
          fetchJSON(`/products/${handle}.js`)
        ]);
        const vendor = prod.vendor?.trim();
        const isFreeGift = tags.includes('free_gift');
        const hasMatch = isFreeGift && activeVendors.has(vendor);
        if (!hasMatch) {
          try { await removeLineByKey(g.key); changed = true; } catch (e) { }
        }
      }

      // 4) Eliminar regalos si se baja del umbral
      if (removeIfBelow && !atOrAbove) {
        for (const g of giftLines) {
          try { await removeLineByKey(g.key); changed = true; } catch (e) { }
        }
      }
    } catch (e) {
      console.error('[GiftGuard] normalizeCart error', e);
    } finally {
      _normalizing = false;
      if (changed) try { await refreshCartSections(); } catch (e) { }
      if (_normalizeScheduled) {
        _normalizeScheduled = false;
        setTimeout(() => normalizeFromDom(), NORMALIZE_DEBOUNCE_MS);
      }
    }
  };

  // ---------------------
  // 🧩 UI & INIT
  // ---------------------
  const refreshCartSections = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const itemsWrap = qs('#main-cart-items');
    const footerWrap = qs('#main-cart-footer');
    const itemsId = itemsWrap?.dataset?.id;
    const footerId = footerWrap?.dataset?.id;

    if (!itemsId && !footerId) return;

    const [itemsHtml, footerHtml] = await Promise.all([
      itemsId ? fetch(`${root}cart?section_id=${itemsId}`).then(r => r.ok && r.text()) : null,
      footerId ? fetch(`${root}cart?section_id=${footerId}`).then(r => r.ok && r.text()) : null
    ]);

    if (itemsHtml) {
      const tmp = document.createElement('div');
      tmp.innerHTML = itemsHtml;
      const newItems = tmp.querySelector(`#main-cart-items[data-id="${itemsId}"] .js-contents`);
      const oldItems = document.querySelector(`#main-cart-items[data-id="${itemsId}"] .js-contents`);
      if (newItems && oldItems) oldItems.replaceWith(newItems);
    }

    if (footerHtml) {
      const tmp = document.createElement('div');
      tmp.innerHTML = footerHtml;
      const newFooter = tmp.querySelector(`#main-cart-footer[data-id="${footerId}"] .js-contents`) || tmp.querySelector(`#main-cart-footer[data-id="${footerId}"]`);
      const oldFooter = document.querySelector(`#main-cart-footer[data-id="${footerId}"] .js-contents`) || document.querySelector(`#main-cart-footer[data-id="${footerId}"]`);
      if (newFooter && oldFooter) oldFooter.replaceWith(newFooter);
    }

    initGift(document);
    setupObserver();
    await filterGiftOptionsByProviders(document);
  };

  const setLoading = (btn, on) => {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!on);
    btn.disabled = !!on;
  };

  const lockGiftQtyUI = (root = document) => {
    qsa('tr[data-is-gift="true"]', root).forEach(row => {
      const input = qs('input.quantity__input', row);
      const btnMinus = qs('.quantity__button[name="minus"]', row);
      const btnPlus = qs('.quantity__button[name="plus"]', row);

      if (input) {
        input.value = '1';
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
        input.setAttribute('min', '1');
        input.setAttribute('max', '1');
        input.setAttribute('step', '0');
        input.addEventListener('input', () => input.value = '1');
        input.addEventListener('change', () => input.value = '1');
      }

      if (btnMinus) btnMinus.setAttribute('disabled', 'true');
      if (btnPlus) btnPlus.setAttribute('disabled', 'true');
    });
  };

  const getSettingsFromDom = (root = document) => {
    const box = qs('.cart-gift[data-gift]', root);
    if (!box) return null;
    return {
      thresholdCents: parseInt(box.getAttribute('data-threshold-cents') || '0', 10),
      removeIfBelow: box.getAttribute('data-remove-below') === 'true'
    };
  };

  const normalizeFromDom = (root = document) => {
    const cfg = getSettingsFromDom(root);
    if (cfg) normalizeCart(cfg.thresholdCents, cfg.removeIfBelow);
  };

  const animateGiftBars = (root = document) => {
    qsa('.cart-gift', root).forEach((box) => {
      const fill = qs('.cart-gift__fill', box);
      if (!fill) return;
      const val = box?.style?.getPropertyValue('--gift-progress') || getComputedStyle(box).getPropertyValue('--gift-progress') || box?.dataset?.progress || '0';
      const pct = Math.min(100, Math.max(0, parseFloat(val))) + '%';
      fill.style.width = '0%';
      requestAnimationFrame(() => requestAnimationFrame(() => fill.style.width = pct));
    });
  };

  const bindChooseHandlers = (root = document) => {
    qsa('[data-gift-add]', root).forEach(btn => {
      if (btn.__giftBound) return;
      btn.__giftBound = true;
      btn.addEventListener('click', async () => {
        const variantId = btn.getAttribute('data-variant-id');
        const wrap = btn.closest('[data-gift-options]');
        const currentKey = wrap?.getAttribute('data-current-gift-key');
        const currentVariant = wrap?.getAttribute('data-current-gift-variant');
        setLoading(btn, true);
        try {
          if (currentKey && currentVariant !== variantId) await removeLineByKey(currentKey);
          if (!currentKey || currentVariant !== variantId) await addVariant(variantId);
          await refreshCartSections();
        } catch (e) {
          console.error('[GiftGuard] Gift select error:', e);
          alert("No puedes añadir este regalo para tu carrito.");
        } finally {
          setLoading(btn, false);
        }
      }, { passive: true });
    });
  };

  const bindRemoveHandler = (root = document) => {
    qsa('[data-gift-remove]', root).forEach(btn => {
      if (btn.__giftBound) return;
      btn.__giftBound = true;
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-current-gift-key');
        if (!key) return;
        setLoading(btn, true);
        try {
          await removeLineByKey(key);
          await refreshCartSections();
        } catch (e) {
          console.error('[GiftGuard] Gift remove error:', e);
          location.reload();
        } finally {
          setLoading(btn, false);
        }
      }, { passive: true });
    });
  };

  const setupObserver = () => {
    const target = qs('#main-cart-items');
    if (!target || target.__giftObserver) return;

    const mo = new MutationObserver(() => {
      requestAnimationFrame(async () => {
        initGift(target);
        setTimeout(() => normalizeFromDom(target), NORMALIZE_DEBOUNCE_MS);
        await filterGiftOptionsByProviders(target);
      });
    });

    mo.observe(target, { childList: true, subtree: true });
    target.__giftObserver = mo;
  };

  const initGiftCarousel = (root = document) => {
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    qsa('[data-gift-carousel]', root).forEach(wrap => {
      const rail = qs('.cart-gift__rail', wrap);
      const prev = qs('[data-gift-carousel-prev]', wrap);
      const next = qs('[data-gift-carousel-next]', wrap);
      if (!rail || !prev || !next) return;

      const scrollBy = () => Math.max(Math.round(rail.clientWidth * 0.9), 1);

      const updateArrows = () => {
        const maxScroll = rail.scrollWidth - rail.clientWidth;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= maxScroll - 2;
      };

      prev.addEventListener('click', () => rail.scrollBy({ left: -scrollBy(), behavior }));
      next.addEventListener('click', () => rail.scrollBy({ left: scrollBy(), behavior }));

      rail.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') rail.scrollBy({ left: scrollBy(), behavior });
        else if (e.key === 'ArrowLeft') rail.scrollBy({ left: -scrollBy(), behavior });
      });

      rail.addEventListener('scroll', updateArrows, { passive: true });
      new ResizeObserver(updateArrows).observe(rail);
      updateArrows();
    });
  };

  const initGift = (root = document) => {
    animateGiftBars(root);
    bindChooseHandlers(root);
    bindRemoveHandler(root);
    lockGiftQtyUI(root);
    initGiftCarousel(root);
    filterGiftOptionsByProviders(root);
  };

  // ---------------------
  // 🧪 INIT
  // ---------------------
  const launch = async (root = document) => {
    initGift(root);
    setupObserver();
    setTimeout(() => normalizeFromDom(root), NORMALIZE_DEBOUNCE_MS);
    await filterGiftOptionsByProviders(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => launch(document), { once: true });
  } else {
    launch(document);
  }

  document.addEventListener('shopify:section:load', (evt) => launch(evt.target || document));
})();
